export enum VideoProvider {
  youtube = 'youtube',
  rutube = 'rutube',
  vk = 'vk'
}

const providersUrls = {
  [VideoProvider.youtube]: ['youtu.be', 'youtube.com'],
  [VideoProvider.rutube]: ['rutube.ru'],
  [VideoProvider.vk]: ['vk.com']
} as const;

export interface ParsedVideoUrl {
  provider: VideoProvider;
  isShorts: boolean;
  videoId?: string;
  rutubeId?: string;
  rutubeQueryParams?: string | null;
  vkParams?: { oid: string; id: string };
}

const extractYoutubeId = (url: string): string | null => {
  const id =
    url.split('v=')[1]?.split('&')[0] ||
    url.split('be/')[1]?.split('&')[0] ||
    url.split('youtu.be/')[1]?.split('&')[0];

  if (id && id.length === 11) {
    return id;
  }

  return null;
};

export const parseVideoUrl = (url: string): ParsedVideoUrl | null => {
  // https://www.youtube.com/watch?v=Or8sxqdMxmk || https://www.youtube.com/shorts/yDeQXkvvp6k
  if (providersUrls[VideoProvider.youtube].some(item => url.includes(item))) {
    const id = extractYoutubeId(url);

    if (!id) {
      console.error('Invalid YouTube video ID', url);
      return null;
    }

    return {
      provider: VideoProvider.youtube,
      isShorts: url.includes('shorts'),
      videoId: id
    };
  }

  // eslint-disable-next-line max-len
  // https://rutube.ru/video/3b5ad75d5a9ac75eb5cdd2056ec30294/ || https://rutube.ru/shorts/a9c971463dc58af87c4be18999d6bb88/ || https://rutube.ru/video/private/a9c971463dc58af87c4be18999d6bb88/?p=Wmk909p1DoOdjXezI7BJlw
  if (providersUrls[VideoProvider.rutube].some(item => url.includes(item))) {
    const id = url
      .split('?')[0]
      .split('/')
      .reverse()
      .find(a => a.length);

    const queryParams = url?.split('/?')?.[1];

    return {
      provider: VideoProvider.rutube,
      isShorts: url.includes('shorts'),
      rutubeId: id,
      rutubeQueryParams: queryParams ?? null
    };
  }

  // eslint-disable-next-line max-len
  // https://vk.com/video/@delikateska/all?z=video-28590902_456240301%2Fclub28590902 || https://vk.com/video/@delikateska/clips?z=clip-28590902_456240305%2Fclub28590902
  if (providersUrls[VideoProvider.vk].some(item => url.includes(item))) {
    let oid: string | undefined = '';
    let id: string | undefined = '';
    if (url.includes('/clips')) {
      // eslint-disable-next-line
      const regexp = /clip\-([0-9]{1,})\_([0-9]{1,})/;
      const params = regexp.exec(url);
      oid = params?.[1];
      id = params?.[2];
    } else {
      // eslint-disable-next-line
      const regexp = /video\-([0-9]{1,})\_([0-9]{1,})/;
      const params = regexp.exec(url);
      oid = params?.[1];
      id = params?.[2];
    }

    if (!oid || !id) {
      return null;
    }

    return {
      provider: VideoProvider.vk,
      isShorts: url.includes('/clips'),
      vkParams: { oid, id }
    };
  }

  return null;
};

export const getVideoEmbedUrl = (parsed: ParsedVideoUrl): string | null => {
  switch (parsed.provider) {
    case VideoProvider.youtube:
      return parsed.videoId
        ? `https://www.youtube.com/embed/${parsed.videoId}`
        : null;
    case VideoProvider.rutube:
      return parsed.rutubeId
        ? `https://rutube.ru/play/embed/${parsed.rutubeId}`
        : null;
    case VideoProvider.vk:
      return parsed.vkParams
        ? `https://vk.com/video_ext.php?oid=-${parsed.vkParams.oid}&id=${parsed.vkParams.id}`
        : null;
    default:
      return null;
  }
};

export const getVideoThumbnailUrl = (parsed: ParsedVideoUrl): string | null =>
  parsed.provider === VideoProvider.youtube && parsed.videoId
    ? `https://i.ytimg.com/vi/${parsed.videoId}/hqdefault.jpg`
    : null;

export const getVideoContentUrl = (parsed: ParsedVideoUrl): string | null => {
  switch (parsed.provider) {
    case VideoProvider.youtube:
      return parsed.videoId
        ? `https://www.youtube.com/watch?v=${parsed.videoId}`
        : null;
    case VideoProvider.rutube: {
      if (!parsed.rutubeId) {
        return null;
      }

      const query = parsed.rutubeQueryParams
        ? `?${parsed.rutubeQueryParams}`
        : '';

      return `https://rutube.ru/video/${parsed.rutubeId}/${query}`;
    }
    case VideoProvider.vk:
      return parsed.vkParams
        ? `https://vk.com/video-${parsed.vkParams.oid}_${parsed.vkParams.id}`
        : null;
    default:
      return null;
  }
};

export const getRutubeVideoApiUrl = (parsed: ParsedVideoUrl): string | null => {
  if (parsed.provider !== VideoProvider.rutube || !parsed.rutubeId) {
    return null;
  }

  const query = parsed.rutubeQueryParams ? `?${parsed.rutubeQueryParams}` : '';

  return `https://rutube.ru/api/video/${parsed.rutubeId}/${query}`;
};
